import type { Tables } from "@msuite/utils/types";
import clsx from "clsx";
import React, { type PropsWithChildren } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { Divider } from "../divider";
import { InputWrapper } from "../form/input-wrapper";
import { useTranslation } from "../localization";
import { FileCardLocal } from "./file-card-local";
import { FileUploadStatusIsFetchingDefaultFiles } from "./file-upload-status-is-fetching-default-files";
import { FileUploadStatusPending } from "./file-upload-status-pending";
import { FileUploadStatusUploading } from "./file-upload-status-uploading";
import type { FileUploadController } from "./use-file-upload";

/** Props Interface */
export interface FileUploadProps extends PropsWithChildren {
	controller: FileUploadController;
	isRequired?: boolean;
	renderFile: (file: Tables<"files">) => React.ReactNode;
	withLabel?: boolean;
	accept?: Accept;
	hint?: string;
}

export function FileUpload({
	controller,
	isRequired = false,
	renderFile,
	withLabel = true,
	accept,
	hint,
	children,
}: FileUploadProps) {
	/** Hooks */
	const { t } = useTranslation();
	const dropzone = useDropzone({
		onDrop: controller.handleOnAddFiles,
		accept,
	});

	/** Render */
	return (
		<InputWrapper
			errors={{}}
			label={withLabel ? t("file_upload.upload") : undefined}
			isRequired={isRequired}
		>
			<input
				{...dropzone.getInputProps()}
				multiple
				className="hidden"
				data-cy="file-upload"
			/>
			<div
				{...(dropzone.getRootProps() as Omit<ReturnType<typeof dropzone.getRootProps>, "role">)}
				role="none"
				className={clsx(
					"card py-8",
					"cursor-pointer",
					"gap-4",
					"hover:[&_.dropzone-icon]:scale-[0.9]",
					"active:[&_.dropzone-icon]:scale-[0.95]",
					"relative",
					dropzone.isDragActive && "border-dashed!",
					dropzone.isDragAccept && "border-primary-500!",
					dropzone.isDragReject && "border-red-500!",
				)}
			>
				<FileUploadStatusIsFetchingDefaultFiles controller={controller} />
				<FileUploadStatusPending
					controller={controller}
					hint={hint}
				/>
				<FileUploadStatusUploading controller={controller} />
			</div>
			{children}
			{controller.files.length > 0 || controller.defaultFiles.length > 0 ? (
				<div className="flex flex-col gap-2">
					<Divider className="my-2" />
					{controller.defaultFiles.length > 0 ? (
						<React.Fragment>
							<p className="label">{t("file_upload.previous_files")}</p>
							{controller.defaultFiles.map((file) => renderFile(file))}
						</React.Fragment>
					) : null}
					{controller.files?.map((file, index) => (
						<FileCardLocal
							file={file}
							key={`${file.lastModified}-${index}`}
							onRemove={controller.handleOnRemove}
							height={null}
							width={null}
						/>
					))}
				</div>
			) : null}
		</InputWrapper>
	);
}
